import React, { useState, useEffect } from "react";
import {
  Modal,
  FormInput,
  FormSelect,
  FormTextarea,
  Button,
  FileUpload,
  ConfirmationModal,
  SearchableSelect,
} from "../../../../components/ui";
import { InspectionAddForm } from "./InspectionAddForm";
import { SiteVisitWizard } from "./SiteVisitWizard";
import InitialQuotationForm from "../../../client_contact_stages/Initial-Quotation/modals/InitialQuotationForm";
import { CustomerAcceptanceForm } from "../../../client_contact_stages/CustomerAcceptance/modals/CustomerAcceptanceForm";

import toast from "react-hot-toast";
import { showApiError } from "@/lib/toast";
import api from "@/services/api";
import { createMaterialRequest } from "../../../../services/VerveMaterialRequestService";
import {
  Calendar,
  DollarSign,
  CheckCircle,
  Info,
  ShieldCheck,
  User,
  AlertTriangle,
} from "lucide-react";
import {
  appointmentSchema,
  quotationSchema,
  subsidySchema,
  assignmentSchema,
} from "../validations/leadStage.schema";

export const ChangeStatusForm = ({
  pendingStatus,
  onClose,
  onConfirm,
  stages,
  teamMembers = [],
  user,
}) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [targetStatus, setTargetStatus] = useState("");

  const stageNoteFields = {
    contacted: "contactedNote",
    appointment_scheduled: "appointmentNote",
    site_visit: "inspectionNote",
    initial_quotation: "quotationNote",
    approved: "approvedNote",
    drop: "droppedNote",
    new: "notes",
  };

  const getTargetField = (status) => stageNoteFields[status] || "notes";

  const roleStr = (user?.role || "")
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, "-");
  const isSalesExec = roleStr === "sales-exec" || roleStr === "sale-exec";

  // Filter only sales executives (removed location restriction so any executive can be assigned)
  const salesExecs = (teamMembers || []).filter((u) => {
    const uRole = (u.role || "")
      .toLowerCase()
      .trim()
      .replace(/[-_\s]+/g, "-");
    const isExec =
      uRole === "sales-exec" ||
      uRole === "sale-exec" ||
      uRole === "sales-executive" ||
      uRole === "sales";

    return isExec;
  });

  const emailVal = (pendingStatus?.lead?.email || "").trim().toLowerCase();
  const isMissingEmail =
    !emailVal || ["n/a", "undefined", "null"].includes(emailVal);
  const isInvalidFormat =
    !isMissingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
  const isSuspicious =
    !isMissingEmail && !isInvalidFormat && emailVal.split("@")[0].length < 3;
  const hasEmailIssue = isMissingEmail || isInvalidFormat || isSuspicious;

  useEffect(() => {
    if (pendingStatus) {
      const { lead, targetStatus } = pendingStatus;
      setTargetStatus(targetStatus);
      setShowEmailConfirm(false);

      // Initialize form based on target status
      if (targetStatus === "appointment_scheduled") {
        setFormData({
          appointmentDate: lead.appointmentDate
            ? new Date(lead.appointmentDate).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0],
          notes: "",
        });
      } else if (
        targetStatus === "initial_quotation"
      ) {
        const capacity = lead.VerveCapacityRequested || lead.capacity || 1;
        setFormData({
          quotationAmount: lead.quotationAmount || capacity * 70000,
          VerveCapacity: capacity,
          notes: "",
          quotationFile: [],
        });
      } else if (targetStatus === "site_visit") {
        setFormData({
          propertyAddress:
            lead.propertyAddress || lead.address || lead.addressLine1 || "",
          propertySize: lead.propertySize || lead.propertyType || "",
          projectType: lead.projectType || "",
          propertyType: lead.propertyType || "",
          floorDetails: lead.floorDetails || "",
          existingPowerConsumption: lead.existingPowerConsumption || "",
          VerveCapacityRequested:
            lead.VerveCapacityRequested || lead.capacity || "",
          existingPowerPaid: lead.existingPowerPaid || "",
          notes: "",
        });
      } else if (targetStatus === "assigned") {
        setFormData({
          assignedTo: isSalesExec
            ? user?._id
            : lead.assignedTo?._id || lead.assignedTo || "",
          notes: "",
        });
      } else {
        setFormData({ notes: "" });
      }
    }
  }, [pendingStatus]);

  if (!pendingStatus) return null;

  const { lead, targetStatus: stageKey } = pendingStatus;
  const stage = stages.find((s) => s.key === stageKey) || { label: stageKey };
  const fullName =
    lead?.customerName ||
    `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() ||
    "No Name";
  const currentStatus = lead?.leadStatus || lead?.status || "new";

  const processSubmission = async (data) => {
    // Schema-based validations FIRST
    const targetField = getTargetField(targetStatus);
    const submissionData = { ...data };
    let validationResult = { success: true, data: submissionData };

    if (targetStatus === "appointment_scheduled") {
      validationResult = appointmentSchema.safeParse(submissionData);
    } else if (
      targetStatus === "initial_quotation"
    ) {
      validationResult = quotationSchema.safeParse(submissionData);
    } else if (targetStatus === "assigned") {
      validationResult = assignmentSchema.safeParse(submissionData);
    }

    if (!validationResult.success) {
      const errMsg =
        validationResult.error?.errors?.[0]?.message ||
        validationResult.error?.issues?.[0]?.message ||
        "Validation failed";
      toast.error(errMsg);
      return;
    }

    const finalData = { ...validationResult.data, status: targetStatus };

    // Move to projects if status is 'delivery'
    if (targetStatus === 'delivery' || targetStatus === 'DELIVERY') {
      finalData.isMovedToProject = true;
    }

    // Email check removed for initial_quotation

    setLoading(true);
    if (showEmailConfirm) setShowEmailConfirm(false);

    try {
      // Map notes to specific fields
      if (finalData.notes) {
        finalData[targetField] = finalData.notes;
        finalData.activityNote = finalData.notes;
      }

      if (
        targetStatus === "initial_quotation"
      ) {
        await api.post("/initial-quotations", {
          leadId: lead._id,
          type: targetStatus,
          items: finalData.items,
          quotationAmount: finalData.quotationAmount,
        });

        // Add dual call to material requests using the flat items array
        const allItemsFlat = [];
        if (finalData.items && Array.isArray(finalData.items)) {
          finalData.items.forEach(roomBlock => {
            ['items', 'blumItems', 'priceListItems', 'customItems'].forEach(arrKey => {
              if (roomBlock[arrKey] && Array.isArray(roomBlock[arrKey])) {
                roomBlock[arrKey].forEach(item => {
                  allItemsFlat.push({
                    ...item,
                    ...(item.rawData || {})
                  });
                });
              }
            });
          });
        }
        await createMaterialRequest({
          leadId: lead._id,
          items: allItemsFlat,
          estimatedPrice: finalData.quotationAmount
        });
      } else if (targetStatus === 'site_visit') {
        await onConfirm(finalData);
        window.dispatchEvent(new Event('refetchNotifications'));
        onClose();
        return;
      }

      await onConfirm(finalData);
      window.dispatchEvent(new Event('refetchNotifications'));
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await processSubmission(formData);
  };

  const commonNotes = (
    <FormTextarea
      label="Transition Notes / Activity Comment"
      placeholder="What happened? Why the change?"
      value={formData.notes || ""}
      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
      rows={3}
    />
  );

  const renderFields = () => {
    switch (targetStatus) {
      case "appointment_scheduled":
        //update Halimunnisa 09-06-2026
        //Added a new specialized date picker for scheduling appointments, which includes a calendar icon and stylized display. The date input is hidden and triggered by clicking on the component, providing a more intuitive and visually appealing way to select appointment dates.
        return (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-start gap-3">
              <div className="p-2 bg-blue-100 text-brand-blue rounded-lg">
                <Calendar size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-brand-blue">
                  Schedule Appointment
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Select a date for the site visit or meeting.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Appointment Date"
                type="date"
                value={formData.appointmentDate || ""}
                onChange={(e) =>
                  setFormData({ ...formData, appointmentDate: e.target.value })
                }
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </div>
            {commonNotes}
          </div>
        );

      case "site_visit":
        return (
          <SiteVisitWizard
            initialData={{
              ...lead,
              propertyAddress:
                lead.propertyAddress || lead.address || lead.addressLine1 || "",
              propertySize: lead.propertySize || lead.propertyType || "",
              projectType: lead.projectType || "",
              propertyType: lead.propertyType || "",
            }}
            lead={lead}
            fullName={fullName}
            commonNotes={commonNotes}
            notesValue={formData.notes}
            onSubmit={(data) => {
              const updatedData = { ...data, notes: formData.notes };
              setFormData(updatedData);
              processSubmission(updatedData);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "inspection_done":
        return (
          <InspectionAddForm
            initialData={lead}
            onSubmit={(data) => {
              setFormData(data);
              processSubmission(data);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "initial_quotation":
        return (
          <InitialQuotationForm
            initialData={lead}
            isFinal={false}
            commonNotes={commonNotes}
            notesValue={formData.notes}
            onSubmit={(data) => {
              const updatedData = { ...data, notes: formData.notes };
              setFormData(updatedData);
              processSubmission(updatedData);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "customer_acceptance":
        return (
          <InitialQuotationApprovalForm
            initialData={lead}
            commonNotes={commonNotes}
            notesValue={formData.notes}
            onSubmit={(data) => {
              const updatedData = {
                ...data,
                notes: formData.notes,
                initialQuotationApproval: {
                  ...lead.initialQuotationApproval,
                  isRequested: true,
                  requestNotes: formData.notes,
                  designerUser: data.designerUser || null,
                  managerUser: data.managerUser || null,
                  ownerUser: data.ownerUser || null,
                  designer: { status: data.designerUser ? "pending" : "skipped", notes: "", actedAt: null, approvedBy: null },
                  manager: { status: data.managerUser ? "pending" : "skipped", notes: "", actedAt: null, approvedBy: null },
                  owner: { status: "pending", notes: "", actedAt: null, approvedBy: null }
                },
              };
              setFormData(updatedData);
              processSubmission(updatedData);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "cust_acceptance":
        return (
          <CustomerAcceptanceForm
            initialData={lead}
            commonNotes={commonNotes}
            notesValue={formData.notes}
            onSubmit={(data) => {
              // ======================================================
              // Updated by: Halimunnisa
              // Feature: Customer Acceptance Workflow
              // Purpose: Customer Acceptance workflow transition
              // Accepted -> Design
              // Rejected -> Rejected/Stopped (remains in cust_acceptance)
              // Revision Requested -> Initial Quotation Approval
              // ======================================================
              const status = data.status || "Pending";
              let targetLeadStatus = "cust_acceptance";
              let actionLog = "Customer Acceptance updated";

              if (status === "Accepted") {
                targetLeadStatus = "design";
                actionLog = "Customer accepted quotation";
              } else if (status === "Revision Requested") {
                targetLeadStatus = "customer_acceptance";
                actionLog =
                  "Customer requested revision. Returned to Initial Quotation Approval.";
              }

              const updatedData = {
                ...data,
                status: targetLeadStatus, // Allows overriding the default targetStatus
                notes:
                  actionLog +
                  (formData.notes ? ` | Remarks: ${formData.notes}` : ""),
                customerAcceptance: {
                  ...(lead.customerAcceptance || {}),
                  status: status,
                  remarks: formData.notes,
                  actedAt: new Date(),
                  actedBy: user?._id,
                },
              };
              processSubmission(updatedData);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );




      case "approved":
        return (
          <div className="space-y-4 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-2">
              <CheckCircle size={32} />
            </div>
            <div>
              <h4 className="font-bold text-slate-800">Approve Lead?</h4>
              <p className="text-xs text-slate-500 mt-1">
                This will mark the lead as approved and move it to the next
                stage of the project.
              </p>
            </div>
            {commonNotes}
          </div>
        );

      case "assigned":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex items-center gap-3">
              <User size={50} className="text-brand-blue" />
              <div className="flex flex-col gap-1">
                <div className="text-sm font-bold text-brand-blue">
                  Assign Sales Executive
                </div>
                <p className="text-xs text-blue">
                  You are moving this lead to the Assigned stage. Please select
                  an executive to take ownership.
                </p>
              </div>
            </div>
            <FormSelect
              label={isSalesExec ? "Assigned To (You)" : "Select Executive"}
              placeholder="Select Sales Executive"
              value={formData.assignedTo || ""}
              onChange={(e) =>
                !isSalesExec &&
                setFormData({ ...formData, assignedTo: e.target.value })
              }
              disabled={isSalesExec}
              options={salesExecs.map((u) => ({ value: u._id, label: u.name }))}
              required
            />
            {commonNotes}
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
              <Info size={18} className="text-slate-400" />
              <div className="text-sm font-bold text-slate-700">
                Transition to {stage.label}
              </div>
            </div>
            {commonNotes}
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={!!pendingStatus}
      onClose={onClose}
      title={`Transition: ${fullName}`}
      size={
        [
          "inspection_done",
          "site_visit",
          "initial_quotation",
          "customer_acceptance"
        ].includes(targetStatus)
          ? "xl"
          : "sm"
      }
    >
      <div className="space-y-5">


        <ConfirmationModal
          isOpen={showEmailConfirm}
          onClose={() => setShowEmailConfirm(false)}
          onConfirm={() => processSubmission(confirmData)}
          title="Proceed without automated email?"
          message={`We couldn't find a valid email for this customer. You can still generate the quotation and download it manually from the View Lead modal after confirming.`}
          confirmText="Confirm & Download Manually"
          cancelText="Back"
          variant="warning"
        />

        {[
          "inspection_done",
          "site_visit",
          "initial_quotation",
          "customer_acceptance",
          "cust_acceptance",
        ].includes(targetStatus) ? (
          renderFields()
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {renderFields()}

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                variant="orange"
              >
                Confirm {stage.label}
              </Button>
              <Button variant="secondary" onClick={onClose} className="px-6">
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
